<script setup>
import { v4 as createUUID } from "uuid";
import { computed, onBeforeUnmount, onMounted, reactive } from "vue";
import EventBus from "@/utils/EventBus";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import AlertModal from "@/components/modals/AlertModal.vue";
import ShareModal from "@/components/modals/ShareModal.vue";
import RestockNotificationFormModal from "@/components/modals/RestockNotificationFormModal.vue";
import QnaConfirmingPasswordModal from "@/components/modals/QnaConfirmingPasswordModal.vue";
import QnaWritingFormModal from "@/components/modals/QnaWritingFormModal.vue";
import QnaEditingFormModal from "@/components/modals/QnaEditingFormModal.vue";
import ReviewFormModal from "@/components/modals/ReviewFormModal.vue";
import ReviewEditFormModal from "@/components/modals/ReviewEditFormModal.vue";
import OrderPhoneInquiryModal from "@/components/modals/OrderPhoneInquiryModal.vue";
import OrderExchangeOrRefundFormModal from "@/components/modals/OrderExchangeOrRefundFormModal.vue";
import OtpTokenModal from "@/components/modals/OtpTokenModal.vue";

const alertModalProps = reactive({});
const alertModalKeys = computed(() => Object.keys(alertModalProps));
const confirmModalProps = reactive({});
const confirmModalKeys = computed(() => Object.keys(confirmModalProps));
const shareModalProps = reactive({});
const shareModalKeys = computed(() => Object.keys(shareModalProps));
const restockNotificationFormModalProps = reactive({});
const restockNotificationFormModalKeys = computed(() =>
  Object.keys(restockNotificationFormModalProps)
);
const qnaConfirmingPasswordModalProps = reactive({});
const qnaConfirmingPasswordModalKeys = computed(() =>
  Object.keys(qnaConfirmingPasswordModalProps)
);
const qnaWritingFormModalProps = reactive({});
const qnaWritingFormModalKeys = computed(() =>
  Object.keys(qnaWritingFormModalProps)
);
const qnaEditingFormModalProps = reactive({});
const qnaEditingFormModalKeys = computed(() =>
  Object.keys(qnaEditingFormModalProps)
);

const reviewFormModalProps = reactive({});
const reviewFormModalKeys = computed(() => Object.keys(reviewFormModalProps));

const reviewEditFormModalProps = reactive({});
const reviewEditFormModalKeys = computed(() =>
  Object.keys(reviewEditFormModalProps)
);

const orderPhoneInquiryModalProps = reactive({});
const orderPhoneInquiryModalKeys = computed(() =>
  Object.keys(orderPhoneInquiryModalProps)
);

const otpTokenModalProps = reactive({});
const otpTokenModalKeys = computed(() => Object.keys(otpTokenModalProps));

const orderExchangeOrRefundFormModalProps = reactive({});
const orderExchangeOrRefundFormModalKeys = computed(() =>
  Object.keys(orderExchangeOrRefundFormModalProps)
);

function openAlertDialogModal({ title, contents, closeText, onClose }) {
  const id = createUUID();
  alertModalProps[id] = {
    title,
    contents,
    closeText,
    onClose: async () => {
      await onClose();
      delete alertModalProps[id];
    },
  };
}

function openConfirmDialogModal({
  title,
  contents,
  confirmText,
  closeText,
  onSubmit,
}) {
  const id = createUUID();
  confirmModalProps[id] = {
    title,
    contents,
    confirmText,
    closeText,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete confirmModalProps[id];
    },
  };
}

function openShareDialogModal({ title, value, closeText, onClose }) {
  const id = createUUID();
  shareModalProps[id] = {
    title,
    value,
    closeText,
    onClose: async () => {
      await onClose();
      delete shareModalProps[id];
    },
  };
}

function openRestockNotificationFormModal({
  productId,
  productName,
  productMainImageUrl,
  useOption,
  productOptionGroups,
  productOptionCombines,
  onClose,
}) {
  const id = createUUID();
  restockNotificationFormModalProps[id] = {
    productId,
    productName,
    productMainImageUrl,
    useOption,
    productOptionGroups,
    productOptionCombines,
    onClose: async () => {
      await onClose();
      delete restockNotificationFormModalProps[id];
    },
  };
}

function openQnaConfirmingPasswordFormModal({ qnaId, onSubmit, onClose }) {
  const id = createUUID();
  qnaConfirmingPasswordModalProps[id] = {
    qnaId,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete qnaConfirmingPasswordModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete qnaConfirmingPasswordModalProps[id];
    },
  };
}

function openQnaWritingFormModal({
  productTextName,
  productId,
  onSubmit,
  onClose,
}) {
  const id = createUUID();
  qnaWritingFormModalProps[id] = {
    productTextName,
    productId,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete qnaWritingFormModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete qnaWritingFormModalProps[id];
    },
  };
}

function openQnaEditFormModal({
  productTextName,
  qnaId,
  qnaContent,
  qnaIsSecret,
  onSubmit,
  onClose,
}) {
  const id = createUUID();
  qnaEditingFormModalProps[id] = {
    productTextName,
    qnaId,
    qnaContent,
    qnaIsSecret,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete qnaEditingFormModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete qnaEditingFormModalProps[id];
    },
  };
}

function openReviewFormModal({
  reviewTarget,
  mainImage,
  productTextName,
  onSubmit,
  onClose,
}) {
  const id = createUUID();
  reviewFormModalProps[id] = {
    reviewTarget,
    mainImage,
    productTextName,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete reviewFormModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete reviewFormModalProps[id];
    },
  };
}

function openReviewEditFormModal({ reviewTarget, onSubmit, onClose }) {
  const id = createUUID();
  reviewEditFormModalProps[id] = {
    reviewTarget,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete reviewEditFormModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete reviewEditFormModalProps[id];
    },
  };
}

function openOrderPhoneInquiryModal({
  callQnaName,
  callQnaNumber,
  callQnaTime,
  onSubmit,
  onClose,
}) {
  const id = createUUID();
  orderPhoneInquiryModalProps[id] = {
    callQnaName,
    callQnaNumber,
    callQnaTime,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete orderPhoneInquiryModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete orderPhoneInquiryModalProps[id];
    },
  };
}

function openOrderExchangeOrRefundFormModal({
  orderId,
  orderItemId,
  orderItemName,
  onSubmit,
  onClose,
}) {
  const id = createUUID();
  orderExchangeOrRefundFormModalProps[id] = {
    orderId,
    orderItemId,
    orderItemName,
    onSubmit: async (result) => {
      await onSubmit(result);
      delete orderExchangeOrRefundFormModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete orderExchangeOrRefundFormModalProps[id];
    },
  };
}

function openOtpTokenModal({ onSubmit, onClose }) {
  const id = createUUID();
  otpTokenModalProps[id] = {
    onSubmit: async (result) => {
      await onSubmit(result);
      delete otpTokenModalProps[id];
    },
    onClose: async () => {
      await onClose();
      delete otpTokenModalProps[id];
    },
  };
}

onMounted(() => {
  EventBus.on("alert", openAlertDialogModal);
  EventBus.on("confirm", openConfirmDialogModal);
  EventBus.on("share", openShareDialogModal);
  EventBus.on(
    "showRestockNotificationFormModal",
    openRestockNotificationFormModal
  );
  EventBus.on(
    "showQnaConfirmingPasswordFormModal",
    openQnaConfirmingPasswordFormModal
  );
  EventBus.on("showQnaWritingFormModal", openQnaWritingFormModal);
  EventBus.on("showQnaEditFormModal", openQnaEditFormModal);
  EventBus.on("showReviewFormModal", openReviewFormModal);
  EventBus.on("showReviewEditFormModal", openReviewEditFormModal);
  EventBus.on("showOrderPhoneInquiryModal", openOrderPhoneInquiryModal);
  EventBus.on(
    "showOrderExchangeOrRefundFormModal",
    openOrderExchangeOrRefundFormModal
  );
  EventBus.on("showOtpTokenModal", openOtpTokenModal);
});
onBeforeUnmount(() => {
  EventBus.off("alert", openAlertDialogModal);
  EventBus.off("confirm", openConfirmDialogModal);
  EventBus.off("share", openShareDialogModal);
  EventBus.off(
    "showRestockNotificationFormModal",
    openRestockNotificationFormModal
  );
  EventBus.off(
    "showQnaConfirmingPasswordFormModal",
    openQnaConfirmingPasswordFormModal
  );
  EventBus.off("showQnaWritingFormModal", openQnaWritingFormModal);
  EventBus.off("showQnaEditFormModal", openQnaEditFormModal);
  EventBus.off("showReviewFormModal", openReviewFormModal);
  EventBus.off("showReviewEditFormModal", openReviewEditFormModal);
  EventBus.off("showOrderPhoneInquiryModal", openOrderPhoneInquiryModal);
  EventBus.off(
    "showOrderExchangeOrRefundFormModal",
    openOrderExchangeOrRefundFormModal
  );
  EventBus.off("showOtpTokenModal", openOtpTokenModal);
});
</script>

<template>
  <share-modal
    v-for="shareModalKey in shareModalKeys"
    :key="`share-modal-${shareModalKey}`"
    v-bind="shareModalProps[shareModalKey]"
    @close="shareModalProps[shareModalKey].onClose"
  />
  <restock-notification-form-modal
    v-for="restockNotificationFormModalKey in restockNotificationFormModalKeys"
    :key="`restock-notification-form-modal-${restockNotificationFormModalKey}`"
    v-bind="restockNotificationFormModalProps[restockNotificationFormModalKey]"
    @close="
      restockNotificationFormModalProps[restockNotificationFormModalKey].onClose
    "
  />
  <qna-confirming-password-modal
    v-for="qnaConfirmingPasswordModalKey in qnaConfirmingPasswordModalKeys"
    :key="`qna-confirming-password-modal-${qnaConfirmingPasswordModalKey}`"
    v-bind="qnaConfirmingPasswordModalProps[qnaConfirmingPasswordModalKey]"
    @submit="
      qnaConfirmingPasswordModalProps[qnaConfirmingPasswordModalKey].onSubmit
    "
    @close="
      qnaConfirmingPasswordModalProps[qnaConfirmingPasswordModalKey].onClose
    "
  />
  <qna-writing-form-modal
    v-for="qnaWritingFormModalKey in qnaWritingFormModalKeys"
    :key="`qna-writing-form-modal-${qnaWritingFormModalKey}`"
    v-bind="qnaWritingFormModalProps[qnaWritingFormModalKey]"
    @submit="qnaWritingFormModalProps[qnaWritingFormModalKey].onSubmit"
    @close="qnaWritingFormModalProps[qnaWritingFormModalKey].onClose"
  />
  <qna-editing-form-modal
    v-for="qnaEditingFormModalKey in qnaEditingFormModalKeys"
    :key="`qna-editing-form-modal-${qnaEditingFormModalKey}`"
    v-bind="qnaEditingFormModalProps[qnaEditingFormModalKey]"
    @submit="qnaEditingFormModalProps[qnaEditingFormModalKey].onSubmit"
    @close="qnaEditingFormModalProps[qnaEditingFormModalKey].onClose"
  />
  <review-form-modal
    v-for="reviewFormModalKey in reviewFormModalKeys"
    :key="`review-form-modal-${reviewFormModalKey}`"
    v-bind="reviewFormModalProps[reviewFormModalKey]"
    @submit="reviewFormModalProps[reviewFormModalKey].onSubmit"
    @close="reviewFormModalProps[reviewFormModalKey].onClose"
  />
  <review-edit-form-modal
    v-for="reviewEditFormModalKey in reviewEditFormModalKeys"
    :key="`review-edit-form-modal-${reviewEditFormModalKey}`"
    v-bind="reviewEditFormModalProps[reviewEditFormModalKey]"
    @submit="reviewEditFormModalProps[reviewEditFormModalKey].onSubmit"
    @close="reviewEditFormModalProps[reviewEditFormModalKey].onClose"
  />
  <order-phone-inquiry-modal
    v-for="orderPhoneInquiryModalKey in orderPhoneInquiryModalKeys"
    :key="`order-phone-inquiry-modal-${orderPhoneInquiryModalKey}`"
    v-bind="orderPhoneInquiryModalProps[orderPhoneInquiryModalKey]"
    @submit="orderPhoneInquiryModalProps[orderPhoneInquiryModalKey].onSubmit"
    @close="orderPhoneInquiryModalProps[orderPhoneInquiryModalKey].onClose"
  />
  <order-exchange-or-refund-form-modal
    v-for="orderExchangeOrRefundFormModalKey in orderExchangeOrRefundFormModalKeys"
    :key="`order-exchange-or-refund-form-modal-${orderExchangeOrRefundFormModalKey}`"
    v-bind="
      orderExchangeOrRefundFormModalProps[orderExchangeOrRefundFormModalKey]
    "
    @submit="
      orderExchangeOrRefundFormModalProps[orderExchangeOrRefundFormModalKey]
        .onSubmit;
    "
    @close="
      orderExchangeOrRefundFormModalProps[orderExchangeOrRefundFormModalKey]
        .onClose;
    "
  />
  <otp-token-modal
    v-for="otpTokenModalKey in otpTokenModalKeys"
    :key="`otp-token-modal-${otpTokenModalKey}`"
    v-bind="otpTokenModalProps[otpTokenModalKey]"
    @submit="otpTokenModalProps[otpTokenModalKey].onSubmit"
    @close="otpTokenModalProps[otpTokenModalKey].onClose"
  />
  <alert-modal
    v-for="alertModalKey in alertModalKeys"
    :key="`confirm-modal-${alertModalKey}`"
    v-bind="alertModalProps[alertModalKey]"
    @close="alertModalProps[alertModalKey].onClose"
  />
  <confirm-modal
    v-for="confirmModalKey in confirmModalKeys"
    :key="`confirm-modal-${confirmModalKey}`"
    v-bind="confirmModalProps[confirmModalKey]"
    @submit="confirmModalProps[confirmModalKey].onSubmit"
  />
</template>
